import { defineListView } from "@/base/list/defineListView";
import type { AuthedBlobRequester, AuthedRequester } from "@/lib/brokerAuth";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import { formatBondDate } from "@/lib/bondValueDisplay";
import {
  formatInvoiceAmount,
  getInvoice,
  invoiceBondLabel,
  invoiceHref,
  listAccountInvoices,
  readInvoiceStatus,
  type InvoiceRow,
} from "@/lib/invoices";
import { invoiceDocumentIdOf } from "@/lib/accountBillingList";
import { orderInvoices } from "@/lib/submissionBilling";
import { InvoiceStatusChip } from "@/components/InvoiceStatusChip";
import { DocumentDownload } from "@/components/surety-account/DocumentDownload";

// BRK-S426 — the submission page's Billing tab as a BaseListView definition,
// embedded under the tab's tiles (BRK-S255: every list is one). There is no
// order-scoped invoice read, so the ACCOUNT's
// (`GET /api/broker/accounts/{id}/invoices`, PMS-S507, pinned) is read once per
// mount and filtered here by `bondSubmissionId`; paging is cut from it. No
// search, filter or sort: a submission has a handful of invoices.
//
// The October wireframe's seven columns (BRK-S502, a `wide` list): Invoice ·
// Bond · Description (PMS's served `description`, PMS-S854) ·
// Amount · Issued · Due · Status, and the PDF download as the row action.
// NEVER rendered (D5, BRK-S187): a Pay now button, a payment type
// (ACH / Check / Wire), a payment method or reference.

const WRAP = "[overflow-wrap:anywhere]";

export function submissionBillingListView({
  request,
  fetchBlob,
  accountId,
  orderId,
  onRows,
}: {
  request: AuthedRequester;
  /** The authenticated file read, for each row's Download. */
  fetchBlob: AuthedBlobRequester;
  accountId: string;
  orderId: string;
  /** This order's rows, each time the list reads them — the tiles add up
   *  exactly what the list shows, from the same read. */
  onRows: (rows: InvoiceRow[]) => void;
}) {
  /** The row carries no `documentId`: read the invoice, then its PDF through
   *  the account's document route, the one download door (BRK-S146). */
  async function downloadInvoice(row: InvoiceRow) {
    const detail = await getInvoice(request, row.id);
    return downloadAccountDocument(
      fetchBlob,
      accountId,
      invoiceDocumentIdOf(detail),
    );
  }

  return defineListView<InvoiceRow>({
    endpoint: `/api/broker/accounts/${accountId}/invoices`,
    noun: "invoices",
    wide: true,
    title: "Invoices",
    columns: [
      {
        id: "invoice",
        header: "Invoice",
        kind: "title",
        cell: (r) => <span className={`tabular-nums ${WRAP}`}>{r.number}</span>,
      },
      {
        id: "bond",
        header: "Bond",
        cell: (r) => (
          <span className={WRAP}>
            {Array.isArray(r.bonds) && r.bonds.length > 0
              ? r.bonds.map((b) => invoiceBondLabel(b)).join(", ")
              : "—"}
          </span>
        ),
      },
      {
        id: "description",
        header: "Description",
        cell: (r) => (
          <span className={WRAP}>
            {typeof r.description === "string" && r.description.trim() !== ""
              ? r.description
              : "—"}
          </span>
        ),
      },
      {
        id: "amount",
        header: "Amount",
        align: "right",
        cell: (r) => (
          <span className="tabular-nums">
            {/* A formatted string is never accepted from the wire (D-10). */}
            {typeof r.amount === "number" && Number.isFinite(r.amount)
              ? formatInvoiceAmount(r.amount)
              : "—"}
          </span>
        ),
      },
      {
        id: "issued",
        header: "Issued",
        cell: (r) => (
          <span className="tabular-nums">{formatBondDate(r.issuedOn)}</span>
        ),
      },
      {
        id: "due",
        header: "Due",
        cell: (r) => (
          <span className="tabular-nums">{formatBondDate(r.dueOn)}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: (r) => <InvoiceStatusChip status={readInvoiceStatus(r.status)} />,
      },
    ],
    rowKey: (r) => r.id,
    rowHref: (r) => invoiceHref(r.id),
    rowLabel: (r) => `Invoice ${r.number}`,
    rowActions: (r) => (
      <DocumentDownload
        label={`Download invoice ${r.number}`}
        fallbackName={`${r.number}.pdf`}
        download={() => downloadInvoice(r)}
      />
    ),
    pageKey: "billPage",
    pageSize: 10,
    empty: {
      title:
        "Nothing billed yet. Premium is invoiced when final bonds are issued.",
    },
    load: async ({ limit, offset }) => {
      const rows = orderInvoices(
        await listAccountInvoices(request, accountId),
        orderId,
      );
      onRows(rows);
      return {
        items: rows.slice(offset, offset + limit),
        total: rows.length,
        limit,
        offset,
      };
    },
  });
}
